/* Public /campaigns/{slug} — a full campaign page with hero media,
 * story, live progress bar, raised/donors/goal, and a Donate CTA
 * that forwards to /donate?campaign={slug} so the checkout tags the
 * donation to this campaign automatically. */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowRight, Users, DollarSign, Target, Loader2, MapPin, X, Maximize2 } from 'lucide-react';
import FloatingDonateTab from '../components/FloatingDonateTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
})}`;

const CampaignDetailPage = () => {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  // Flyer/hero image lightbox — a separate boolean because the hero
  // media is a single image (not indexed like additional_photos).
  const [flyerOpen, setFlyerOpen] = useState(false);

  // ESC-to-close for either open modal. Also locks page scroll while
  // any lightbox is open so mobile users don't accidentally pan the
  // page behind the overlay.
  useEffect(() => {
    const anyOpen = flyerOpen || lightboxIdx !== null;
    if (!anyOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setFlyerOpen(false); setLightboxIdx(null); }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [flyerOpen, lightboxIdx]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/campaigns/${slug}`);
        setC(r.data);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  if (notFound || !c) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex flex-col items-center justify-center px-6 text-center py-20"
           data-testid="campaign-not-found">
        <Heart className="w-14 h-14 text-[#BA1826]/40 mb-4" />
        <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-2">Campaign not available</h1>
        <p className="text-[#4B5563] mb-6 max-w-md">
          This campaign is no longer active or may have ended. Browse other active campaigns
          or donate to the general fund.
        </p>
        <div className="flex gap-2">
          <Link to="/campaigns" className="btn-primary text-sm inline-flex items-center gap-1.5">
            All campaigns <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/donate"
                className="text-sm px-4 py-2 border border-[#0A0A0A] text-[#0A0A0A] rounded hover:bg-[#0A0A0A] hover:text-white transition-colors">
            General donation
          </Link>
        </div>
      </div>
    );
  }

  const pct = c.goal > 0 ? Math.min(100, (c.total_raised / c.goal) * 100) : 0;
  const isVideo = /\.(mp4|webm|mov)(\?|$)/i.test(c.media_url || '');
  const donateHref = `/donate?campaign=${encodeURIComponent(c.slug)}`;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <FloatingDonateTab />
      {/* Hero */}
      <section className="bg-[#0A0A0A] text-white relative border-b-4 border-[#BA1826]" data-testid="campaign-detail-hero">
        <div className="max-w-6xl mx-auto px-6 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-start items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#BA1826] font-bold mb-3">
              {(c.type || 'campaign').replace(/_/g, ' ')}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4" data-testid="campaign-name">
              {c.name}
            </h1>
            {(c.country_name || c.location) && (
              <p className="text-sm text-white/70 flex items-center gap-1.5 mb-4">
                <MapPin className="w-4 h-4 text-[#BA1826]" />
                {c.country_name}{c.location ? ` — ${c.location}` : ''}
              </p>
            )}
            {c.description && (
              <p className="text-base text-white/80 leading-relaxed whitespace-pre-line" data-testid="campaign-description">
                {c.description}
              </p>
            )}
          </div>
          {c.media_url && (
            isVideo ? (
              // Videos: keep inline player, unchanged. Per spec, only
              // flyers (still images) get the click-to-expand treatment.
              <div className="rounded-lg overflow-hidden bg-black/40 aspect-video" data-testid="campaign-media">
                <video src={c.media_url} className="w-full h-full object-cover"
                       controls playsInline preload="metadata" />
              </div>
            ) : (
              // Flyer: click-to-expand button. `object-contain` on a
              // fixed-height container that preserves whatever aspect
              // ratio the flyer was uploaded in (portrait, landscape,
              // or square all render fully — no cropping). System-wide:
              // any future campaign flyer inherits this automatically.
              <button
                type="button"
                onClick={() => setFlyerOpen(true)}
                data-testid="campaign-flyer-btn"
                aria-label="View full flyer"
                className="group relative w-full rounded-lg overflow-hidden bg-black/40 focus:outline-none focus:ring-2 focus:ring-[#BA1826]"
              >
                <img
                  src={c.media_url}
                  alt={c.name}
                  data-testid="campaign-media"
                  className="w-full h-[420px] sm:h-[500px] object-contain bg-black/60 transition-transform duration-500 group-hover:scale-[1.02]"
                />
                {/* Subtle hover cue */}
                <span
                  data-testid="campaign-flyer-cue"
                  className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white text-[11px] uppercase tracking-wide font-bold px-2.5 py-1.5 rounded opacity-90 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  Click to view full flyer
                </span>
              </button>
            )
          )}
        </div>
      </section>

      {/* Progress + Donate */}
      <section className="max-w-4xl mx-auto px-6 py-10">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 sm:p-8" data-testid="campaign-progress-card">
          {c.goal > 0 && (
            <>
              <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wide font-bold text-[#6B7280]">Raised</p>
                  <p className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A]" data-testid="campaign-raised">
                    {money(c.total_raised)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wide font-bold text-[#6B7280]">Goal</p>
                  <p className="font-heading text-xl font-bold text-[#4B5563]">{money(c.goal)}</p>
                </div>
              </div>
              <div className="relative h-2.5 bg-[#F1F5F9] rounded-full overflow-hidden mb-3">
                <div className="absolute inset-y-0 left-0 bg-[#BA1826] rounded-full transition-all"
                     style={{ width: `${pct}%` }}
                     data-testid="campaign-progress-bar"></div>
              </div>
              <p className="text-xs text-[#6B7280]">
                <span className="font-bold text-[#0A0A0A]">{pct.toFixed(1)}%</span> of goal reached
              </p>
            </>
          )}

          {/* Metrics row */}
          <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-[#E5E7EB]">
            <div>
              <div className="text-[10px] uppercase tracking-wide font-bold text-[#6B7280] flex items-center gap-1 mb-0.5">
                <Users className="w-3 h-3" /> Donors
              </div>
              <p className="font-heading text-lg font-bold text-[#0A0A0A]" data-testid="campaign-donors">
                {c.donor_count || 0}
              </p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide font-bold text-[#6B7280] flex items-center gap-1 mb-0.5">
                <DollarSign className="w-3 h-3" /> Raised
              </div>
              <p className="font-heading text-lg font-bold text-[#0A0A0A]">{money(c.total_raised)}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide font-bold text-[#6B7280] flex items-center gap-1 mb-0.5">
                <Target className="w-3 h-3" /> Goal
              </div>
              <p className="font-heading text-lg font-bold text-[#0A0A0A]">
                {c.goal > 0 ? money(c.goal) : '—'}
              </p>
            </div>
          </div>

          {/* Donate CTA */}
          <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
            <Link to={donateHref} data-testid="campaign-donate-btn"
                  className="btn-primary w-full text-base inline-flex items-center justify-center gap-2 py-3">
              <Heart className="w-5 h-5" /> Donate to this campaign
            </Link>
            <p className="text-[11px] text-center text-[#6B7280] mt-2">
              Every dollar credited directly to this campaign. Tax-deductible under 501(c)(3).
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/campaigns"
                data-testid="back-to-campaigns"
                className="text-sm text-[#4B5563] hover:text-[#BA1826] inline-flex items-center gap-1">
            <ArrowRight className="w-4 h-4 rotate-180" /> View all campaigns
          </Link>
        </div>
      </section>

      {/* Additional photos — grid rendered only when the campaign has
          images uploaded. Simple responsive grid: 2 cols on mobile,
          3 cols on desktop. Clicking opens a lightbox. */}
      {Array.isArray(c.additional_photos) && c.additional_photos.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-12" data-testid="campaign-photos-section">
          <div className="mb-4">
            <h2 className="font-heading text-2xl font-bold text-[#0A0A0A]">Campaign Photos</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3"
               data-testid="campaign-photos-grid">
            {c.additional_photos.map((url, i) => (
              <button key={i} type="button" onClick={() => setLightboxIdx(i)}
                data-testid={`campaign-photo-${i}`}
                className="group relative aspect-square overflow-hidden rounded-md bg-[#F3F4F6] focus:outline-none focus:ring-2 focus:ring-[#BA1826]">
                <img src={url} alt={`${c.name} — photo ${i + 1}`} loading="lazy"
                     className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox — plain overlay with prev/next. Kept minimal. */}
      {lightboxIdx !== null && Array.isArray(c.additional_photos) && (
        <div className="fixed inset-0 z-[95] bg-black/90 flex items-center justify-center p-4"
             onClick={() => setLightboxIdx(null)}
             data-testid="campaign-photo-lightbox">
          <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded"
            data-testid="lightbox-close-btn"
            aria-label="Close">
            <X className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation();
              setLightboxIdx((lightboxIdx - 1 + c.additional_photos.length) % c.additional_photos.length); }}
            className="absolute left-2 sm:left-6 text-white p-2 hover:bg-white/10 rounded"
            data-testid="lightbox-prev-btn"
            aria-label="Previous">
            <ArrowRight className="w-6 h-6 rotate-180" />
          </button>
          <img src={c.additional_photos[lightboxIdx]}
               alt={`Photo ${lightboxIdx + 1}`}
               onClick={(e) => e.stopPropagation()}
               className="max-h-[85vh] max-w-[92vw] object-contain rounded"
               data-testid="lightbox-image" />
          <button onClick={(e) => { e.stopPropagation();
              setLightboxIdx((lightboxIdx + 1) % c.additional_photos.length); }}
            className="absolute right-2 sm:right-6 text-white p-2 hover:bg-white/10 rounded"
            data-testid="lightbox-next-btn"
            aria-label="Next">
            <ArrowRight className="w-6 h-6" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs"
               data-testid="lightbox-counter">
            {lightboxIdx + 1} / {c.additional_photos.length}
          </div>
        </div>
      )}

      {/* Flyer lightbox — full-screen modal for the hero flyer image.
          Click-outside closes it (backdrop). ESC also closes it (see
          the effect above). The image itself scrolls on very tall
          portrait flyers so mobile users can pan without losing the
          close button. */}
      {flyerOpen && c.media_url && !isVideo && (
        <div
          className="fixed inset-0 z-[95] bg-black/95 overflow-auto"
          onClick={() => setFlyerOpen(false)}
          data-testid="campaign-flyer-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${c.name} flyer`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFlyerOpen(false); }}
            className="fixed top-4 right-4 z-10 bg-black/60 text-white p-2 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
            data-testid="flyer-lightbox-close-btn"
            aria-label="Close full-flyer view"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="min-h-full flex items-center justify-center p-4 sm:p-8">
            <img
              src={c.media_url}
              alt={`${c.name} — full flyer`}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full sm:max-w-[92vw] h-auto object-contain rounded shadow-2xl"
              data-testid="flyer-lightbox-image"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDetailPage;
